import { useState } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  type Middleware,
} from "@floating-ui/react";
import {
  DEFAULT_POPOVER_CONTENT_OFFSET,
  resolvePopoverContentOffset,
  type PopoverContentOffset,
} from "./popoverContentOffset";
import type { PopoverPlacement, PopoverTrigger } from "./types";

interface UsePopoverOptions {
  disabled?: boolean;
  delay?: number;
  placement?: PopoverPlacement;
  trigger?: PopoverTrigger;
  onVisibilityChange?: (isVisible: boolean) => void;
  middleware?: Middleware[];
  contentOffset?: PopoverContentOffset;
  open?: boolean;
}

export const createDefaultPopoverMiddleware = (
  placement: PopoverPlacement,
  contentOffset: PopoverContentOffset = DEFAULT_POPOVER_CONTENT_OFFSET,
): Middleware[] => [
  offset(resolvePopoverContentOffset(placement, contentOffset)),
  flip({
    fallbackAxisSideDirection: "start",
  }),
  shift({ padding: 8 }),
];

export const usePopover = (options: UsePopoverOptions = {}) => {
  const {
    disabled = false,
    delay = 150,
    placement = "bottom-start",
    trigger = "hover",
    onVisibilityChange,
    middleware,
    contentOffset = DEFAULT_POPOVER_CONTENT_OFFSET,
    open: controlledOpen,
  } = options;

  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: controlledOpen !== undefined ? controlledOpen : isOpen,
    onOpenChange: (v) => {
      setIsOpen(v);
      onVisibilityChange?.(v);
    },
    placement,
    middleware:
      middleware ?? createDefaultPopoverMiddleware(placement, contentOffset),
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: { open: 0, close: delay },
    enabled: !disabled && trigger === "hover",
  });

  const focus = useFocus(context, {
    enabled: !disabled && trigger === "focus",
  });

  const click = useClick(context, {
    enabled: !disabled && trigger === "click",
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: trigger === "click" ? "dialog" : "tooltip",
  });

  const interactions = [dismiss, role];

  if (trigger === "hover") {
    interactions.push(hover);
  }
  if (trigger === "focus") {
    interactions.push(focus);
  }
  if (trigger === "click") {
    interactions.push(click);
  }

  const { getReferenceProps, getFloatingProps } = useInteractions(interactions);

  return {
    isOpen: controlledOpen !== undefined ? controlledOpen : isOpen && !disabled,
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    FloatingPortal,
    close: () => {
      setIsOpen(false);
      onVisibilityChange?.(false);
    },
  };
};
